import React, { Children, useState, useEffect, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import Header from './header'
import HeaderClass from './headerClass'
import HeaderContainer from './headerContainer'
import Body from './body'
import BodyClass from './bodyClass'
import { classNames, useGlobalId, iconProps } from '@zambezi/sdk/common'

/**
 * React component to render an accordion
 * @param {import('./interfaces').AccordionProps} props
 * @param {import('react').RefObject<import('./interfaces').AccordionAPI>} ref
 * @returns {JSX.Element}
 */
function accordion (
  {
    openItems,
    children,
    allowMultipleOpen,
    onChange,
    Header,
    Body,
    withPadding,
    className,
    Container,
    iconProps
  },
  ref
) {
  const [active, setActive] = useState(
    openItems != null
      ? _getActiveIndexesFromOpenItems(openItems)
      : _getActiveIndexesFromChildren()
  )
  const accordionId = useGlobalId('')
  /** @type {import('react').RefObject<Object<number,HTMLElement>>} */
  const toggles = useRef({})
  /** @type {import('react').RefObject<HTMLElement>} */
  const containerRef = useRef()
  const apiRef = useRef(getAPI())

  useEffect(componentWillReceiveProps, [openItems])
  useEffect(updateRef)

  return (
    <Container
      className={classNames('zb-accordion', className)}
      ref={containerRef}
      role='presentation'
    >
      {_getItems()}
    </Container>
  )

  /**
   * @returns {import('./interfaces').AccordionAPI}
   */
  function getAPI () {
    return Object.assign({}, containerRef.current, {
      closeAll,
      openAll,
      getOpenItems,
      element: containerRef.current
    })
  }

  function updateRef () {
    if (ref) {
      apiRef.current = getAPI()
      const api = apiRef.current
      if (typeof ref === 'function') {
        ref(api)
      } else if ('current' in ref) {
        ref.current = api
      }
    }
  }

  /**
   * @returns {number[]}
   */
  function closeAll () {
    setActive([])
  }

  function openAll () {
    const totalHeaders = children.length / 2
    const headersIndexes = Array.from({ length: totalHeaders })
      .join(',')
      .split(',')
      .map((i, index) => index * 2)

    setActive(headersIndexes)
  }

  function getOpenItems (activeItems = active) {
    return activeItems.map(item => item / 2).sort((a, b) => a - b)
  }

  function componentWillReceiveProps () {
    if (openItems != null) {
      setActive(_getActiveIndexesFromOpenItems(openItems))
    }
  }

  function handleHeaderKeyDown (headerIndex, e) {
    const nbOfHeaders = children.length / 2

    switch (e.key) {
      case 'ArrowDown':
        return focusToggle(e, (headerIndex + 1) % nbOfHeaders)
      case 'ArrowUp':
        return focusToggle(e, (headerIndex - 1 + nbOfHeaders) % nbOfHeaders)
      case 'Home':
        return focusToggle(e, 0)
      case 'End':
        return focusToggle(e, nbOfHeaders - 1)
      default:
        return false
    }
  }

  /**
   * Focus on the toggle element.
   * @param {import('react').SyntheticEvent<HTMLElement, Event>} e
   * @param {number} index
   */
  function focusToggle (e, index) {
    toggles.current[index].focus()
    e.preventDefault()
  }

  function _getActiveIndexesFromOpenItems (openItems) {
    return openItems.map(index => index * 2)
  }

  function _getActiveIndexesFromChildren () {
    const activeIndexes = []

    Children.forEach(children, (child, index) => {
      const isHeader = index % 2 === 0

      if (!isHeader) return

      if (child.props.open) {
        activeIndexes.push(index)
      }
    })

    return activeIndexes
  }

  function _setActive (activeItemIndex, openState) {
    setActive(active => {
      let activeItems = (active || []).concat()
      if (allowMultipleOpen) {
        const pos = activeItems.indexOf(activeItemIndex)
        if (pos !== -1) {
          if (!openState) activeItems.splice(pos, 1)
        } else {
          activeItems.push(activeItemIndex)
        }
      } else {
        // collapse all?
        activeItems =
          activeItems.indexOf(activeItemIndex) !== -1 || !openState
            ? []
            : [activeItemIndex]
      }
      onChange && onChange(apiRef.current, getOpenItems(activeItems))

      return activeItems
    })
  }

  function _getItems () {
    const childrenArray = Children.toArray(children)
    const lastItemIndex = Math.floor(childrenArray.length / 2) - 1

    return childrenArray.map((child, index) => {
      const toggleIndex = index / 2
      const controlId = Math.floor(toggleIndex)
      const headerId = `zb-accordion-${accordionId}-header-${controlId}`
      const bodyId = `zb-accordion-${accordionId}-body-${controlId}`
      const className = classNames(
        { 'zb-accordion-is-last': controlId === lastItemIndex },
        child.props.className
      )

      return index % 2 === 0
        ? (
          <HeaderContainer
            {...child.props}
            className={className}
            id={headerId}
            open={active.indexOf(index) !== -1}
            onToggle={(item, state) => _setActive(index, state)}
            onKeyDown={handleHeaderKeyDown.bind(null, toggleIndex)}
            aria-controls={bodyId}
            aria-expanded={active.indexOf(index) !== -1}
            ref={el => {
              toggles.current[toggleIndex] = el
            }}
            key={index}
            header={Header}
            iconProps={iconProps}
          >
            {child.props.children}
          </HeaderContainer>
          )
        : (
          <Body
            {...child.props}
            className={className}
            id={bodyId}
            open={active.indexOf(index - 1) !== -1}
            withPadding={withPadding}
            aria-labelledby={headerId}
            key={index}
          >
            {child.props.children}
          </Body>
          )
    })
  }
}

const Accordion = forwardRef(accordion)

Accordion.displayName = 'Accordion'
Accordion.propTypes = {
  allowMultipleOpen: PropTypes.bool,
  withPadding: PropTypes.bool,
  openItems: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  onChange: PropTypes.func,
  Container: PropTypes.elementType,
  // compose pairs of items via these
  Header: PropTypes.elementType,
  Body: PropTypes.elementType,
  iconProps
}
Accordion.defaultProps = {
  Header,
  Body,
  withPadding: true,
  Container: 'dl'
}

export { Accordion, HeaderClass as Header, BodyClass as Body }

export default Accordion
